import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./PaymentStatusPage.css";
import confetti from "canvas-confetti";

const PaymentStatusPage = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Verifying your payment...");
  const [paymentId, setPaymentId] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  const checkoutRaw = localStorage.getItem("checkoutDetails");
  const checkoutData = checkoutRaw ? JSON.parse(checkoutRaw) : null;
  const refId = checkoutData?.ndc;
  const isStoppedRef = React.useRef(false);
  const hasCalledRef = useRef(false);

  const handleCheckCurrentPaymentStatus = async () => {
    if (!refId) return { handled: false };

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SECRET}/api/Payment/CheckCurrentPaymentStatus?checkoutId=${refId}`,
      );

      const resultCode = response.data?.result?.code;

      if (
        ["000.100.110", "000.100.112", "000.000.000", "000.000.100"].includes(
          resultCode,
        )
      ) {
        setStatus("success");
        setPaymentId(response.data?.id || null);
        setMessage(response.data?.result?.description || "Payment completed.");
        return { handled: true, final: true };
      } else if (resultCode === "000.200.000") {
        setStatus("loading");
        setMessage("Processing your transaction...");
        return { handled: true, final: false };
      } else {
        setStatus("failed");
        setMessage(response.data?.result?.description || "Payment failed.");
        return { handled: true, final: true };
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        return { handled: false };
      } else {
        setStatus("failed");
        setMessage("Something went wrong.");
        return { handled: true, final: true };
      }
    }
  };

  const checkOrderStatusFallback = async (retry = 0) => {
    if (!refId) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SECRET}/api/Order/OrderPaymentAttemptsStatusCheck?refId=${refId}`,
      );

      const { paymentStatus } = response.data;

      if (paymentStatus === "Success") setStatus("success");
      else if (paymentStatus === "Failed") setStatus("failed");
      else if (paymentStatus === "Pending" && retry < 10) {
        setTimeout(() => checkOrderStatusFallback(retry + 1), 3000);
      } else setStatus("failed");
    } catch {
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (!refId) {
      setStatus("failed");
      setMessage("Invalid payment session.");
      return;
    }

    let interval;

    const startChecking = async () => {
      if (isStoppedRef.current) return;

      const result = await handleCheckCurrentPaymentStatus();

      if (result?.final) {
        isStoppedRef.current = true;
        clearInterval(interval);
        return;
      }

      if (!result?.handled) {
        checkOrderStatusFallback();
      }
    };

    startChecking();

    interval = setInterval(() => {
      if (!isStoppedRef.current) {
        startChecking();
      }
    }, 3000);

    return () => {
      isStoppedRef.current = true;
      clearInterval(interval);
    };
  }, [refId]);

  useEffect(() => {
    if (status === "success" && !hasCalledRef.current) {
      hasCalledRef.current = true;

      const duration = 2000;
      const end = Date.now() + duration;

      const runConfetti = () => {
        confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 } });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 90,
          origin: { x: 1 },
        });

        if (Date.now() < end) requestAnimationFrame(runConfetti);
      };

      runConfetti();

      // 🚀 API call
      const createSubscription = async () => {
        try {
          const planDetails = JSON.parse(localStorage.getItem("planDetails"));
          if (!planDetails) return;

          const payload = {
            restaurantId: planDetails.restaurantId,
            planId: planDetails.planId,
            cycleId: planDetails.cycleId,
          };

          await axios.post(
            `${process.env.REACT_APP_SECRET}/api/SubscriptionPlan/Create-subscriptionplan-for-restaurant`,
            payload,
          );
          localStorage.removeItem("planDetails");
        } catch (error) {
          console.error("Error creating subscription:", error);
        }
      };

      createSubscription();

      let count = 5;
      setRedirectCountdown(count);

      const countdownInterval = setInterval(() => {
        count -= 1;
        setRedirectCountdown(count);

        if (count <= 0) {
          clearInterval(countdownInterval);
          window.location.href = "/";
        }
      }, 1000);
    }
  }, [status]);

  return (
    <div className="ps-container">
      <div className="ps-card">
        <div className="ps-header">
          <div className={`ps-icon ${status}`}>
            {status === "loading" && <div className="spinner"></div>}
            {status === "success" && "✔"}
            {status === "failed" && "✕"}
          </div>
        </div>

        <div className="ps-body">
          <h2>
            {status === "loading" && "Processing Payment"}
            {status === "success" && "Payment Successful"}
            {status === "failed" && "Payment Failed"}
          </h2>
          {status === "success" && (
            <p className="ps-redirect">
              Redirecting to home in {redirectCountdown} second
              {redirectCountdown !== 1 ? "s" : ""}...
            </p>
          )}

          <p>{message}</p>

          {paymentId && (
            <div className="ps-meta">
              <span>Transaction ID</span>
              <strong>{paymentId}</strong>
            </div>
          )}

          {status === "failed" && (
            <button className="ps-btn" onClick={() => window.location.reload()}>
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusPage;
